import { ShippingBox, ShippingDimensions } from '@services/shipping/interfaces/shipping.interfaces';

const getBoxVolume = (box: ShippingBox): number => box.lengthCm * box.widthCm * box.heightCm;

const sortSidesDescending = (sides: number[]): number[] => [...sides].sort((firstSide, secondSide) => secondSide - firstSide);

const describeBox = (box: ShippingBox): string =>
  `${box.label} (${sortSidesDescending([box.lengthCm, box.widthCm, box.heightCm]).join(' × ')} cm, ${box.maxWeightGrams / 1000} kg)`;

const doSidesFitBox = (dimensions: ShippingDimensions, box: ShippingBox): boolean => {
  const boxSides = sortSidesDescending([box.lengthCm, box.widthCm, box.heightCm]);
  const pieceSides = sortSidesDescending([dimensions.lengthCm, dimensions.widthCm, dimensions.heightCm]);
  return pieceSides.every((pieceSide, index) => pieceSide <= boxSides[index]);
};

export function findLargestBox(boxes: ShippingBox[]): ShippingBox | null {
  return boxes.reduce<ShippingBox | null>(
    (largestBox, box) => (largestBox === null || getBoxVolume(box) > getBoxVolume(largestBox) ? box : largestBox),
    null,
  );
}

/**
 * Explains why a piece fits no active box, or returns null when some box holds it (or there is no box to compare with).
 * Mirrors `ShippingPackingLogic.canPackUnit` in the backend: every box is checked, rotating the piece freely.
 */
export function getBoxFitProblem(dimensions: ShippingDimensions, boxes: ShippingBox[]): string | null {
  const largestBox = findLargestBox(boxes);
  if (!largestBox) {
    return null;
  }

  const boxesWithRoom = boxes.filter((box) => doSidesFitBox(dimensions, box));
  if (boxesWithRoom.some((box) => dimensions.weightGrams <= box.maxWeightGrams)) {
    return null;
  }

  if (boxesWithRoom.length === 0) {
    return `Con estas medidas no cabe en ninguna caja activa. La más grande es ${describeBox(largestBox)}.`;
  }

  const strongestBox = boxesWithRoom.reduce((strongest, box) => (box.maxWeightGrams > strongest.maxWeightGrams ? box : strongest));
  return `Con este peso no cabe en ninguna caja activa donde entran sus medidas. La que más aguanta es ${describeBox(strongestBox)}.`;
}
